import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  Rectangle,
  Tooltip,
} from "recharts";
import { Spinner } from "reactstrap";
import clsx from "clsx";
import { ReactComponent as ChevronIcon } from "Assets/icon/icon-chevron.svg";
import { IconButton } from "components/ui";

import { LearnerIndexScoreDataPeriods } from "apps/user/learner/api/types";

import { IndexScoreChange } from "./index-score-container";
import { useTranslation } from "lib/i18n/use-translation";

import styles from "./index-score-graph.module.scss";

const CustomValueShape = (props) => {
  return <Rectangle {...props} radius={[25, 25, 25, 25]} />;
};

const CustomAssessmentValueShape = (props) => {
  const { assessmentValue, biometricsValue } = props;

  let radius;
  if (biometricsValue === null) {
    radius = [25, 25, 25, 25];
  } else if (assessmentValue !== 0 && biometricsValue === 0) {
    radius = [25, 25, 25, 25];
  } else {
    radius = [0, 0, 25, 25];
  }

  return <Rectangle {...props} radius={radius} />;
};

const CustomBiometricsValueShape = (props) => {
  const { assessmentValue, biometricsValue } = props;

  let radius;
  if (assessmentValue === null) {
    radius = [25, 25, 25, 25];
  } else if (assessmentValue === 0 && biometricsValue !== 0) {
    radius = [25, 25, 25, 25];
  } else {
    radius = [25, 25, 0, 0];
  }

  return <Rectangle {...props} radius={radius} />;
};

interface IndexScoreGraphProps {
  dataPeriods: LearnerIndexScoreDataPeriods;
  onChangePeriodLength: (value: "week" | "month" | "year") => void;
  periodLength: "week" | "month" | "year";
  periodIndex: number;
  onClickPreviousPeriod: () => void;
  onClickNextPeriod: () => void;
  displayValues?: "combined" | "assessment" | "biometrics";
  assessmentColor?: string;
  biometricsColor?: string;
  view?: "default" | "compact";
  scoreChangeIconColor?: "red" | "blue";
  moreLoading: boolean;
}

const IndexScoreGraph = ({
  dataPeriods,
  periodIndex,
  onClickNextPeriod,
  onClickPreviousPeriod,
  periodLength,
  onChangePeriodLength,
  displayValues = "combined",
  assessmentColor = "#bf3c57",
  biometricsColor = "#ed6b64",
  view = "default",
  scoreChangeIconColor = "red",
  moreLoading,
}: IndexScoreGraphProps) => {
  const { t } = useTranslation(["index", "common"]);
  const data = dataPeriods?.[periodLength];
  let chartData = [];
  let averageValue;
  let change;
  let periodLabel;
  let hasAssessmentValues = false;
  let hasBiometricsValues = false;
  let hasPreviousLabel = false;
  let hasNextLabel = false;
  let displayNoData = true;

  if (data.length > 0) {
    hasAssessmentValues = data[periodIndex].hasAssessmentValues;
    hasBiometricsValues = data[periodIndex].hasBiometricsValues;
    periodLabel = data[periodIndex].label;
    hasPreviousLabel = data[periodIndex].previousLabel !== null;
    hasNextLabel = data[periodIndex].nextLabel !== null;
    chartData = data?.[periodIndex].dataPoints?.map((value) => {
      if (view === "compact" && displayValues === "combined") {
        return {
          value:
            (value.assessmentValue != null ? value.assessmentValue : 0) +
            (value.biometricsValue != null ? value.biometricsValue : 0),
          assessmentValue: value.assessmentValue,
          biometricsValue: value.biometricsValue,
          label: value.label,
        };
      } else if (view === "default" && displayValues === "combined") {
        return {
          value: value.value,
          label: value.label,
        };
      } else if (displayValues === "assessment") {
        return {
          value:
            (value.assessmentValue != null ? value.assessmentValue : 0) +
            (value.biometricsValue != null ? value.biometricsValue : 0),
          assessmentValue: value.assessmentStandaloneValue,
          biometricsValue: null,
          label: value.label,
        };
      } else if (displayValues === "biometrics") {
        return {
          value:
            (value.assessmentValue != null ? value.assessmentValue : 0) +
            (value.biometricsValue != null ? value.biometricsValue : 0),
          assessmentValue: null,
          biometricsValue: value.biometricsStandaloneValue,
          label: value.label,
        };
      }

      return null;
    });

    if (displayValues === "combined") {
      displayNoData = !hasAssessmentValues && !hasBiometricsValues;
      change = data[periodIndex].valueChange;
      averageValue = data?.[periodIndex].averageValue;
    } else if (displayValues === "assessment") {
      displayNoData = !hasAssessmentValues;
      change = data[periodIndex].assessmentStandaloneValueChange;
      averageValue = data?.[periodIndex].averageAssessmentStandaloneValue;
    } else if (displayValues === "biometrics") {
      displayNoData = !hasBiometricsValues;
      change = data[periodIndex].biometricsStandaloneValueChange;
      averageValue = data?.[periodIndex].averageBiometricsStandaloneValue;
    }
  }

  const handleClickPreviousPeriod = (event) => {
    event.stopPropagation();
    onClickPreviousPeriod();
  };

  const handleClickNextPeriod = (event) => {
    event.stopPropagation();
    onClickNextPeriod();
  };

  const handleChangePeriodLength = (event, value) => {
    event.stopPropagation();
    onChangePeriodLength(value);
  };

  const AvgValueLabel = (props) => {
    const { viewBox, value } = props;

    const x = 80;
    const y = viewBox.y - 9;

    const backgroundWidth = 60;
    const backgroundHeight = 20;
    const backgroundX = x;
    const backgroundY = y;

    return (
      <g>
        {/* Background */}
        <foreignObject
          x={backgroundX}
          y={backgroundY}
          width={backgroundWidth}
          height={backgroundHeight}
        >
          <div
            style={{
              backgroundColor: "rgb(33, 33, 33)",
              borderRadius: "10px",
              padding: "2px",
              fontSize: "12px",
              color: "white",
              textAlign: "center",
              verticalAlign: "middle",
              userSelect: "none",
            }}
          >
            {value}
          </div>
        </foreignObject>
      </g>
    );
  };

  let xLabelInterval = 2;
  let barSize = 20;
  if (periodLength === "month") {
    xLabelInterval = 7;
    barSize = 5;
  } else if (periodLength === "year") {
    xLabelInterval = 1;
    barSize = 15;
  }

  const Legend = ({ style }: { style?: React.CSSProperties }) => (
    <div className={styles["legend"]} style={style}>
      {view === "default" && displayValues === "combined" && (
        <div
          className={styles["legend-items"]}
          style={{
            opacity: hasAssessmentValues || hasBiometricsValues ? 1 : 0.7,
          }}
        >
          <span
            style={{ background: assessmentColor }}
            className={styles["assesment-span"]}
          />
          <p>{t("index:graph.legendIndex")}</p>
        </div>
      )}
      {view !== "default" &&
        (displayValues === "biometrics" || displayValues === "combined") && (
          <div
            className={styles["legend-items"]}
            style={{
              opacity: hasBiometricsValues ? 1 : 0.7,
            }}
          >
            <span
              style={{ background: biometricsColor }}
              className={styles["biometrics-span"]}
            />
            <p>{t("index:graph.legendBiometrics")}</p>
          </div>
        )}
      {view !== "default" &&
        (displayValues === "assessment" || displayValues === "combined") && (
          <div
            className={styles["legend-items"]}
            style={{
              opacity: hasAssessmentValues ? 1 : 0.7,
            }}
          >
            <span
              style={{ background: assessmentColor }}
              className={styles["assesment-span"]}
            />
            <p>{t("index:graph.legendAssessment")}</p>
          </div>
        )}
    </div>
  );

  return (
    <div className={styles["chart-container"]}>
      {view === "compact" && (
        <IndexScoreChange
          change={change}
          className={styles["percent-container"]}
          style={{
            marginLeft: "2.75rem",
            marginBottom: "0.5rem",
            height: "18px",
            display: "flex",
            alignItems: "center",
            gap: "0.3rem",
            fontSize: "0.75rem",
          }}
          iconColor={scoreChangeIconColor}
        />
      )}
      <div className={styles["header"]}>
        <div className={styles["days"]}>
          {periodLabel && (
            <>
              <IconButton
                disabled={moreLoading || !hasPreviousLabel}
                onClick={handleClickPreviousPeriod}
                aria-label="Previous period"
                icon={
                  <ChevronIcon
                    className={clsx(styles["left-chevron"], styles["chevron"])}
                    alt="link arrow"
                  />
                }
              />
              <div className={styles["days-date"]}>
                <span>{periodLabel}</span>
              </div>
              <IconButton
                disabled={!hasNextLabel}
                onClick={handleClickNextPeriod}
                aria-label="Next period"
                icon={
                  <ChevronIcon className={styles["chevron"]} alt="link arrow" />
                }
              />
            </>
          )}
        </div>
        <div className={styles["time-period"]}>
          {view === "default" && (
            <IndexScoreChange
              change={change}
              className={styles["percent-container"]}
            />
          )}

          <div className={styles["time-section"]}>
            <div
              className={
                periodLength === "week" ? styles["time-active"] : styles["time"]
              }
              onClick={(event) => handleChangePeriodLength(event, "week")}
            >
              {t("index:graph.periodWeek")}
            </div>
            <div
              className={
                periodLength === "month"
                  ? styles["time-active"]
                  : styles["time"]
              }
              onClick={(event) => handleChangePeriodLength(event, "month")}
            >
              {t("index:graph.periodMonth")}
            </div>
            <div
              className={
                periodLength === "year" ? styles["time-active"] : styles["time"]
              }
              onClick={(event) => handleChangePeriodLength(event, "year")}
            >
              {t("index:graph.periodYear")}
            </div>
          </div>
        </div>
      </div>
      {view === "default" && <Legend />}
      {moreLoading && (
        <div className={styles["spinner-container"]}>
          <Spinner className="" animation="border" role="status" />
        </div>
      )}
      {!moreLoading && (
        <ResponsiveContainer width="100%" height={265}>
          <BarChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              interval={xLabelInterval}
              angle={0}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              domain={[0, 100]}
              tickCount={6}
            />
            <Tooltip
              cursor={false}
              formatter={(value, name, props) => {
                if (name === "assessmentValue") {
                  return [
                    value,
                    displayValues === "combined"
                      ? t("index:graph.tooltipAssessmentPart")
                      : t("index:graph.tooltipAssessmentScore"),
                  ];
                } else if (name === "biometricsValue") {
                  return [
                    value,
                    displayValues === "combined"
                      ? t("index:graph.tooltipBiometricsPart")
                      : t("index:graph.tooltipBiometricsScore"),
                  ];
                } else if (name === "value") {
                  return [value, t("index:graph.tooltipIndexScore")];
                } else {
                  return value;
                }
              }}
            />
            {view === "default" ? (
              <Bar
                isAnimationActive={false}
                barSize={barSize}
                dataKey="value"
                stackId="indexScore"
                fill={assessmentColor}
                shape={CustomValueShape}
              />
            ) : (
              <>
                <Bar
                  isAnimationActive={false}
                  barSize={barSize}
                  dataKey="assessmentValue"
                  stackId="indexScore"
                  fill={assessmentColor}
                  shape={CustomAssessmentValueShape}
                />
                <Bar
                  isAnimationActive={false}
                  barSize={barSize}
                  dataKey="biometricsValue"
                  stackId="indexScore"
                  fill={biometricsColor}
                  shape={CustomBiometricsValueShape}
                />
              </>
            )}
            <ReferenceLine
              y={averageValue}
              stroke="#212121"
              strokeDasharray="3 3"
              label={<AvgValueLabel value={`AVG. ${averageValue}`} />}
            />
            {displayNoData && (
              <text x="50%" y="50%" textAnchor="middle" fill="#666">
                {t("common:noData")}
              </text>
            )}
          </BarChart>
        </ResponsiveContainer>
      )}

      {view === "compact" && (
        <Legend style={{ paddingBottom: "1rem", marginTop: "0" }} />
      )}
    </div>
  );
};

export default IndexScoreGraph;
